/**
 * Keyboard Editor Page
 * 
 * Allows caregivers to configure which toolbar and action items
 * are visible in the AAC Keyboard, with drag-to-reorder support
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Check, Undo2, RotateCcw, GripVertical, Smile, Hash, MessageCircle, Zap, Globe, AtSign, Delete, Volume2, Mail, ChevronRight, ChevronLeft, Trash2, Keyboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import {
  KeyboardConfig,
  KeyboardToolbarItem,
  KeyboardActionItem,
  loadKeyboardConfig,
  saveKeyboardConfig,
  updateToolbarItem,
  updateActionItem,
  reorderToolbarItems,
  reorderActionItems,
  resetKeyboardConfig,
} from "@/lib/keyboardConfigTypes";
import { useUnsavedEditGuard } from "@/hooks/useUnsavedEditGuard";

const KB_STORAGE_KEY = "keyboardConfig_v1";

const MAX_UNDO_HISTORY = 20;

// Icon map for keyboard items
const KEYBOARD_ICON_MAP: Record<string, React.ElementType> = {
  Smile,
  Hash,
  MessageCircle,
  Zap,
  Globe,
  AtSign,
  Delete,
  Volume2,
  Mail,
  Trash2,
  Keyboard,
};

export default function KeyboardEditor() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  
  const [config, setConfig] = useState<KeyboardConfig>(() => loadKeyboardConfig());
  const [undoHistory, setUndoHistory] = useState<KeyboardConfig[]>([]);
  const [draggedToolbarIndex, setDraggedToolbarIndex] = useState<number | null>(null);
  const [draggedActionIndex, setDraggedActionIndex] = useState<number | null>(null);
  const isUndoing = useRef(false);

  const { markSaved } = useUnsavedEditGuard({
    storageKeys: [KB_STORAGE_KEY],
    onAfterRevert: () => setConfig(loadKeyboardConfig()),
  });

  // Save to localStorage when config changes
  useEffect(() => {
    if (!isUndoing.current) {
      saveKeyboardConfig(config);
    }
  }, [config]);

  // Save to undo history before making changes
  const saveToHistory = useCallback(() => {
    setUndoHistory(prev => {
      const newHistory = [...prev, config];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, [config]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setConfig(previousState);
    saveKeyboardConfig(previousState);
    
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  // Toggle toolbar item enabled state
  const handleToggleToolbar = (itemId: string, enabled: boolean) => {
    saveToHistory();
    const updatedConfig = updateToolbarItem(config, itemId, { enabled });
    setConfig(updatedConfig);
  };

  // Toggle action item enabled state
  const handleToggleAction = (itemId: string, enabled: boolean) => {
    saveToHistory();
    const updatedConfig = updateActionItem(config, itemId, { enabled });
    setConfig(updatedConfig);
  };

  // Handle reset to defaults
  const handleResetToDefault = () => {
    saveToHistory();
    const defaultConfig = resetKeyboardConfig();
    setConfig(defaultConfig);
  };

  // Toolbar drag and drop handlers
  const handleToolbarDragStart = (index: number) => {
    setDraggedToolbarIndex(index);
  };

  const handleToolbarDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedToolbarIndex === null || draggedToolbarIndex === index) return;
    
    saveToHistory();
    const newConfig = reorderToolbarItems(config, draggedToolbarIndex, index);
    setConfig(newConfig);
    setDraggedToolbarIndex(index);
  };

  const handleToolbarDragEnd = () => {
    setDraggedToolbarIndex(null);
  };

  // Action drag and drop handlers
  const handleActionDragStart = (index: number) => {
    setDraggedActionIndex(index);
  };

  const handleActionDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedActionIndex === null || draggedActionIndex === index) return;
    
    saveToHistory();
    const newConfig = reorderActionItems(config, draggedActionIndex, index);
    setConfig(newConfig);
    setDraggedActionIndex(index);
  };

  const handleActionDragEnd = () => {
    setDraggedActionIndex(null);
  };

  // Get sorted items
  const sortedToolbarItems = [...config.toolbarItems].sort((a, b) => a.order - b.order);
  const sortedActionItems = [...config.actionItems].sort((a, b) => a.order - b.order);

  return (
    <MainLayout onBack={() => navigate(-1)}>
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-card/50 border-b border-border">
          <h1 className="text-xl font-bold text-foreground">
            {isRTL ? "עריכת מקלדת" : "Edit keyboard"}
          </h1>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {t("undo")}
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleResetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {t("resetToDefault")}
            </Button>
            
            <Button size="sm" disabled={undoHistory.length === 0} onClick={() => { markSaved(); navigate('/board-editor'); }}>
              <Check className="w-4 h-4 mr-2" />
              {t("doneEditing")}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="max-w-2xl mx-auto space-y-8">
            {/* Description */}
            <p className="text-muted-foreground text-sm">
              {isRTL 
                ? "התאם את הכפתורים שסביב המקלדת. ניתן להפעיל/לכבות כפתורים ולשנות את הסדר שלהם ע\"י גרירה."
                : "Customize the buttons surrounding the keyboard. You can enable/disable buttons and reorder them by dragging."
              }
            </p>

            {/* Action Row Section */}
            <section>
              <h2 className="text-lg font-semibold mb-4 text-foreground">
                {isRTL ? "שורת פעולות עליונה" : "Top Action Row"}
              </h2>
              <div className="space-y-2">
                {sortedActionItems.map((item, index) => {
                  const IconComponent = KEYBOARD_ICON_MAP[item.iconName] || Delete;
                  const label = isRTL ? item.labelHe : item.labelEn;
                  
                  return (
                    <motion.div
                      key={item.id}
                      draggable
                      onDragStart={() => handleActionDragStart(index)}
                      onDragOver={(e) => handleActionDragOver(e, index)}
                      onDragEnd={handleActionDragEnd}
                      className={cn(
                        "flex items-center gap-4 p-4 rounded-xl border-2 bg-card",
                        "transition-all duration-200 cursor-move",
                        draggedActionIndex === index && "opacity-50 scale-95",
                        item.enabled ? "border-primary/50" : "border-border opacity-60"
                      )}
                      whileHover={{ scale: 1.01 }}
                    >
                      <GripVertical className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        item.enabled ? "bg-primary/20" : "bg-muted"
                      )}>
                        <IconComponent className={cn(
                          "w-6 h-6",
                          item.enabled ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      
                      <span className={cn(
                        "flex-1 font-medium",
                        item.enabled ? "text-foreground" : "text-muted-foreground"
                      )}>
                        {label}
                      </span>
                      
                      <Switch
                        checked={item.enabled}
                        onCheckedChange={(checked) => handleToggleAction(item.id, checked)}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </section>

            {/* Toolbar Section */}
            <section>
              <h2 className="text-lg font-semibold mb-4 text-foreground">
                {isRTL ? "סרגל כלים (למטה)" : "Toolbar (Bottom)"}
              </h2>
              <div className="space-y-2">
                {sortedToolbarItems.map((item, index) => {
                  const IconComponent = KEYBOARD_ICON_MAP[item.iconName] || Smile;
                  const label = isRTL ? item.labelHe : item.labelEn;
                  
                  return (
                    <motion.div
                      key={item.id}
                      draggable
                      onDragStart={() => handleToolbarDragStart(index)}
                      onDragOver={(e) => handleToolbarDragOver(e, index)}
                      onDragEnd={handleToolbarDragEnd}
                      className={cn(
                        "flex items-center gap-4 p-4 rounded-xl border-2 bg-card",
                        "transition-all duration-200 cursor-move",
                        draggedToolbarIndex === index && "opacity-50 scale-95",
                        item.enabled ? "border-primary/50" : "border-border opacity-60"
                      )}
                      whileHover={{ scale: 1.01 }}
                    >
                      <GripVertical className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        item.enabled ? "bg-primary/20" : "bg-muted"
                      )}>
                        <IconComponent className={cn(
                          "w-6 h-6",
                          item.enabled ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      
                      <span className={cn(
                        "flex-1 font-medium",
                        item.enabled ? "text-foreground" : "text-muted-foreground"
                      )}>
                        {label}
                      </span>
                      
                      <Switch
                        checked={item.enabled}
                        onCheckedChange={(checked) => handleToggleToolbar(item.id, checked)}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </section>




          </div>
        </div>
      </div>
    </MainLayout>
  );
}
